import { usePos } from '@/store/pos';
import { ApiError, NetworkError } from './client';
import { loadCredentials } from './credentials';

/**
 * Keeping this screen in step with everyone else looking at the same shop.
 *
 * A poll, not a socket. A shop has a couple of people signed in, a few writes a
 * minute at busy times, and a connection that comes and goes; a request carrying
 * the version
 * already held costs one indexed read and answers "nothing new" almost always.
 * A socket would buy a couple of seconds of latency in exchange for reconnect
 * logic, proxy configuration and a second failure mode.
 */

export type LiveStatus = 'starting' | 'live' | 'offline' | 'unauthorised';

export interface LiveState {
  status: LiveStatus;
  /** When the server last answered. */
  lastSeenAt?: number;
  /** Lao, safe to show. Set when the last attempt failed for a stated reason. */
  messageLo?: string;
}

type Listener = (state: LiveState) => void;

let state: LiveState = { status: 'starting' };
const listeners = new Set<Listener>();
/** One request in flight at a time; a second would only duplicate the first. */
let running = false;

export function getLiveState(): LiveState {
  return state;
}

export function subscribeLive(listener: Listener): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}

function update(patch: Partial<LiveState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener(state);
}

/** One round of "has anything changed?". Safe to call at any time. */
export async function tick(): Promise<void> {
  if (running) return;
  if (!(await loadCredentials())) {
    update({ status: 'unauthorised', messageLo: 'ຍັງບໍ່ໄດ້ເຂົ້າສູ່ລະບົບ' });
    return;
  }

  running = true;
  try {
    await usePos.getState().poll();
    update({ status: 'live', lastSeenAt: Date.now(), messageLo: undefined });
  } catch (error) {
    if (error instanceof NetworkError) {
      // The shop's connection dropped. Nothing is lost — nothing was held here
      // to lose — but nothing can be sold until it comes back, and the screens
      // say so rather than pretending.
      update({ status: 'offline', messageLo: 'ຕໍ່ອິນເຕີເນັດບໍ່ໄດ້ ກວດເບິ່ງສັນຍານ' });
    } else if (error instanceof ApiError && error.status === 401) {
      update({ status: 'unauthorised', messageLo: error.messageLo });
    } else if (error instanceof ApiError) {
      update({ status: 'offline', messageLo: error.messageLo });
    } else {
      update({ status: 'offline', messageLo: 'ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ' });
    }
  } finally {
    running = false;
  }
}

/** How often to ask while the app is open and in front of someone. */
const INTERVAL_MS = 5_000;

/**
 * Starts polling. Returns a function that stops it.
 *
 * Three triggers, because each catches something the others miss: the interval
 * for steady state, `online` for a connection coming back, and visibility for a
 * phone taken out of a pocket — which fires no `online` event and would
 * otherwise show yesterday's numbers until the next tick.
 */
export function startLive(): () => void {
  const kick = () => {
    void tick();
  };

  kick();
  const timer = window.setInterval(() => {
    if (document.visibilityState === 'visible') kick();
  }, INTERVAL_MS);

  const onVisible = () => {
    if (document.visibilityState === 'visible') kick();
  };

  window.addEventListener('online', kick);
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    window.clearInterval(timer);
    window.removeEventListener('online', kick);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
